import { getApperClient } from '@/services/apperClient';
import { toast } from 'react-toastify';
import { parseExtendedBrandVoice, serializeExtendedBrandVoice } from './extendedBrandVoiceUtils';
const defaultBrandVoice = {
  profileSummary: "",
  keywords: "",
  hashtags: "",
  voice: "",
  targetAudience: "",
  description: "",
  benefits: "",
  audiences: [],
  campaignObjective: "",
  offerCategory: "",
  brandToneVoice: "",
  topCustomerDesires: "",
  primaryCustomerProblems: "",
  proofAssets: "",
  adStyles: [],
  visualStyles: [],
  awarenessLevel: "",
  problemIntensity: "",
  priorityObjective: "",
  hookStyle: "",
  ctaApproach: "",
  toneStyle: "",
  copyLength: "",
  formattingStyle: "",
  aiCreativity: 60
};

const normalizeBrandVoice = (brandVoice = {}) => ({
  ...defaultBrandVoice,
  ...brandVoice
});

const parseAudiences = (value) => {
  if (Array.isArray(value)) {
    return value.map((aud, idx) => ({
      name: aud?.name || aud?.title || (typeof aud === "string" ? aud : ""),
      description: aud?.description || aud?.details || "",
      isPrimary: Boolean(aud?.isPrimary || aud?.primary || (idx === 0 && aud))
    })).filter(aud => aud.name || aud.description);
  }

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parseAudiences(parsed);
    } catch {
      const lines = value.split(/\n|,/).map(line => line.trim()).filter(Boolean);
      return lines.map((line, idx) => ({
        name: line,
        description: "",
        isPrimary: idx === 0
      }));
    }
  }

  return [];
};

const serializeAudiences = (audiences = []) => {
  if (!Array.isArray(audiences)) return "";
  const trimmed = audiences
    .map((aud, idx) => ({
      name: (aud?.name || "").toString().trim(),
      description: (aud?.description || "").toString().trim(),
      isPrimary: Boolean(aud?.isPrimary || (idx === 0 && aud))
    }))
    .filter(aud => aud.name || aud.description);
  if (trimmed.length === 0) return "";
  return JSON.stringify(trimmed.slice(0, 3));
};

class ClientService {
  constructor() {
    this.tableName = 'client_c';
  }

  async getById(id) {
    try {
      const apperClient = getApperClient();
      if (!apperClient) {
        throw new Error("ApperClient not available");
      }

const response = await apperClient.getRecordById(this.tableName, parseInt(id), {
        fields: [
          {"field": {"Name": "Name"}},
          {"field": {"Name": "company_name_c"}},
          {"field": {"Name": "client_information_c"}},
          {"field": {"Name": "website_url_c"}},
          {"field": {"Name": "brand_voice_profile_summary_c"}},
          {"field": {"Name": "brand_voice_keywords_c"}},
          {"field": {"Name": "brand_voice_hashtags_c"}},
          {"field": {"Name": "brand_voice_voice_c"}},
          {"field": {"Name": "brand_voice_target_audience_c"}},
          {"field": {"Name": "description_c"}},
          {"field": {"Name": "benefits_c"}},
          {"field": {"Name": "audiences_c"}},
          {"field": {"Name": "campaign_objective_c"}},
          {"field": {"Name": "offer_category_c"}},
          {"field": {"Name": "brand_tone_voice_c"}},
          {"field": {"Name": "top_customer_desires_c"}},
          {"field": {"Name": "primary_customer_problems_c"}},
          {"field": {"Name": "proof_assets_c"}},
          {"field": {"Name": "hidden_link_c"}},
          {"field": {"Name": "CreatedOn"}},
          {"field": {"Name": "ModifiedOn"}}
        ]
      });

      if (!response.success) {
        console.error(`Failed to fetch client with Id: ${id}:`, response);
        throw new Error(response.message || "Client not found");
      }

      const client = response.data;
      return {
        Id: client.Id,
        companyName: client.company_name_c || client.Name || "",
        clientInformation: client.client_information_c || "",
        websiteUrl: client.website_url_c || "",
brandVoice: normalizeBrandVoice({
          profileSummary: client.brand_voice_profile_summary_c || "",
          keywords: client.brand_voice_keywords_c || "",
          hashtags: client.brand_voice_hashtags_c || "",
          voice: client.brand_voice_voice_c || "",
          targetAudience: client.brand_voice_target_audience_c || "",
          description: client.description_c || "",
          benefits: client.benefits_c || "",
          audiences: parseAudiences(client.audiences_c),
          campaignObjective: client.campaign_objective_c || "",
          offerCategory: client.offer_category_c || "",
          brandToneVoice: client.brand_tone_voice_c || "",
          topCustomerDesires: client.top_customer_desires_c || "",
          primaryCustomerProblems: client.primary_customer_problems_c || "",
          proofAssets: client.proof_assets_c || "",
          ...parseExtendedBrandVoice(client.hidden_link_c || "")
        }),
        websiteUrl: client.website_url_c || "",
        updatedAt: client.ModifiedOn || new Date().toISOString()
      };
    } catch (error) {
      console.error(`Error fetching client ${id}:`, error);
      throw error;
    }
  }

async getAll() {
    try {
      console.log('CLIENT_GETALL_DEBUG: Starting to fetch all clients from table:', this.tableName);
      const apperClient = getApperClient();
      if (!apperClient) {
        console.error('CLIENT_GETALL_ERROR: ApperClient not available - SDK might not be loaded');
        throw new Error("ApperClient not available - please refresh the page");
      }

      const params = {
fields: [
          {"field": {"Name": "Id"}},
          {"field": {"Name": "Name"}},
          {"field": {"Name": "company_name_c"}},
          {"field": {"Name": "client_information_c"}},
          {"field": {"Name": "website_url_c"}},
          {"field": {"Name": "brand_voice_profile_summary_c"}},
          {"field": {"Name": "brand_voice_keywords_c"}},
          {"field": {"Name": "brand_voice_hashtags_c"}},
          {"field": {"Name": "brand_voice_voice_c"}},
          {"field": {"Name": "brand_voice_target_audience_c"}},
          {"field": {"Name": "description_c"}},
          {"field": {"Name": "benefits_c"}},
          {"field": {"Name": "audiences_c"}},
          {"field": {"Name": "campaign_objective_c"}},
          {"field": {"Name": "offer_category_c"}},
          {"field": {"Name": "brand_tone_voice_c"}},
          {"field": {"Name": "top_customer_desires_c"}},
          {"field": {"Name": "primary_customer_problems_c"}},
          {"field": {"Name": "proof_assets_c"}},
          {"field": {"Name": "hidden_link_c"}},
          {"field": {"Name": "CreatedOn"}},
          {"field": {"Name": "ModifiedOn"}}
        ],
        orderBy: [{"fieldName": "Id", "sorttype": "DESC"}],
        pagingInfo: {"limit": 100, "offset": 0}
      };

      console.log('CLIENT_GETALL_DEBUG: Fetching records with params:', params);
      console.log('CLIENT_GETALL_DEBUG: Using table name:', this.tableName);

      const response = await apperClient.fetchRecords(this.tableName, params);

      console.log('CLIENT_GETALL_DEBUG: API response:', response);

      if (!response.success) {
        console.error("CLIENT_GETALL_ERROR: Failed to fetch clients from database:", response);
        console.error("CLIENT_GETALL_ERROR: Response message:", response.message);
        
        // Return empty array instead of throwing to prevent app crashes
        if (response.message && response.message.includes('502')) {
          console.warn('CLIENT_GETALL_WARNING: Got 502 error, returning empty array to prevent crash');
        }
        return [];
      }

      if (!response.data || response.data.length === 0) {
        console.log('CLIENT_GETALL_DEBUG: No clients found in database - this might be expected for new installations');
        return [];
      }

      console.log('CLIENT_GETALL_DEBUG: Raw client records from database:', response.data);

      const transformedClients = response.data.map(record => {
        const transformed = {
          Id: record.Id,
          companyName: record.company_name_c || record.Name || "",
          clientInformation: record.client_information_c || "",
          websiteUrl: record.website_url_c || "",
brandVoice: normalizeBrandVoice({
            profileSummary: record.brand_voice_profile_summary_c || "",
            keywords: record.brand_voice_keywords_c || "",
            hashtags: record.brand_voice_hashtags_c || "",
            voice: record.brand_voice_voice_c || "",
            targetAudience: record.brand_voice_target_audience_c || "",
            description: record.description_c || "",
            benefits: record.benefits_c || "",
            audiences: parseAudiences(record.audiences_c),
            campaignObjective: record.campaign_objective_c || "",
            offerCategory: record.offer_category_c || "",
            brandToneVoice: record.brand_tone_voice_c || "",
            topCustomerDesires: record.top_customer_desires_c || "",
            primaryCustomerProblems: record.primary_customer_problems_c || "",
            proofAssets: record.proof_assets_c || "",
            ...parseExtendedBrandVoice(record.hidden_link_c || "")
          }),
          websiteUrl: record.website_url_c || "",
          updatedAt: record.ModifiedOn || new Date().toISOString()
        };

        console.log('CLIENT_GETALL_DEBUG: Transformed client:', {
          id: transformed.Id,
          companyName: transformed.companyName,
          original: record
        });

        return transformed;
      });

      console.log('CLIENT_GETALL_DEBUG: All transformed clients:', transformedClients.map(c => ({
        id: c.Id,
        name: c.companyName
      })));

      return transformedClients;
    } catch (error) {
      console.error("CLIENT_GETALL_ERROR: Error fetching clients from database:", error);
      console.error("CLIENT_GETALL_ERROR: Error details:", {
        message: error.message,
        stack: error.stack,
        tableName: this.tableName
      });
      return [];
    }
  }

async create(clientData) {
    try {
      console.log('CLIENT_CREATE_DEBUG: Starting client creation with data:', clientData);
      console.log('CLIENT_CREATE_DEBUG: Using table name:', this.tableName);
      const apperClient = getApperClient();
      if (!apperClient) {
        console.error('CLIENT_CREATE_ERROR: ApperClient not available - SDK might not be loaded');
        throw new Error("ApperClient not available");
      }

      const brandVoice = normalizeBrandVoice(clientData.brandVoice || {});

      // Prepare data with only Updateable fields
const data = {
        Name: clientData.companyName,
        company_name_c: clientData.companyName,
        client_information_c: clientData.clientInformation || "",
        website_url_c: clientData.websiteUrl || "",
        brand_voice_profile_summary_c: brandVoice.profileSummary,
        brand_voice_keywords_c: brandVoice.keywords,
        brand_voice_hashtags_c: brandVoice.hashtags,
        brand_voice_voice_c: brandVoice.voice,
        brand_voice_target_audience_c: brandVoice.targetAudience,
        description_c: brandVoice.description,
        benefits_c: brandVoice.benefits,
        audiences_c: serializeAudiences(brandVoice.audiences),
        campaign_objective_c: brandVoice.campaignObjective,
        offer_category_c: brandVoice.offerCategory,
        brand_tone_voice_c: brandVoice.brandToneVoice,
        top_customer_desires_c: brandVoice.topCustomerDesires,
        primary_customer_problems_c: brandVoice.primaryCustomerProblems,
        proof_assets_c: brandVoice.proofAssets,
        hidden_link_c: serializeExtendedBrandVoice(brandVoice)
      };
      console.log('CLIENT_CREATE_DEBUG: Prepared data for API:', data);

      const response = await apperClient.createRecord(this.tableName, {
        records: [data]
      });

      console.log('CLIENT_CREATE_DEBUG: API response:', response);

      if (!response.success) {
        console.error("CLIENT_CREATE_ERROR: Failed to create client in database:", response);
        console.error("CLIENT_CREATE_ERROR: Response message:", response.message);
        throw new Error(response.message || "Failed to create client");
      }

      if (response.results) {
        const successful = response.results.filter(r => r.success);
        const failed = response.results.filter(r => !r.success);
        
        if (failed.length > 0) {
          console.error(`CLIENT_CREATE_ERROR: Failed to create ${failed.length} clients:`, failed);
          failed.forEach(record => {
            if (record.errors) {
              record.errors.forEach(error => console.error(`${error.fieldLabel}: ${error}`));
            }
          });
        }
        
        if (successful.length > 0) {
          const created = successful[0].data;
          console.log('CLIENT_CREATE_DEBUG: Created record data:', created);
          
          const transformedClient = {
            Id: created.Id,
            companyName: created.company_name_c || created.Name || "",
            clientInformation: created.client_information_c || "",
            websiteUrl: created.website_url_c || "",
brandVoice: normalizeBrandVoice({
              profileSummary: created.brand_voice_profile_summary_c || "",
              keywords: created.brand_voice_keywords_c || "",
              hashtags: created.brand_voice_hashtags_c || "",
              voice: created.brand_voice_voice_c || "",
              targetAudience: created.brand_voice_target_audience_c || "",
              description: created.description_c || "",
              benefits: created.benefits_c || "",
              audiences: parseAudiences(created.audiences_c),
              campaignObjective: created.campaign_objective_c || "",
              offerCategory: created.offer_category_c || "",
              brandToneVoice: created.brand_tone_voice_c || "",
              topCustomerDesires: created.top_customer_desires_c || "",
              primaryCustomerProblems: created.primary_customer_problems_c || "",
              proofAssets: created.proof_assets_c || "",
              ...parseExtendedBrandVoice(created.hidden_link_c || "")
            }),
          websiteUrl: created.website_url_c || "",
            updatedAt: created.ModifiedOn || new Date().toISOString()
          };
          
          console.log('CLIENT_CREATE_DEBUG: Transformed client data:', transformedClient);
          return transformedClient;
        }
      }

      throw new Error("No successful records created");
    } catch (error) {
      console.error("CLIENT_CREATE_ERROR: Error creating client:", error);
      console.error("CLIENT_CREATE_ERROR: Error details:", {
        message: error.message,
        stack: error.stack,
        tableName: this.tableName
      });
      throw error;
    }
  }

async update(id, clientData) {
    try {
      const apperClient = getApperClient();
      if (!apperClient) {
        throw new Error("ApperClient not available");
      }

      const brandVoice = clientData.brandVoice ? normalizeBrandVoice(clientData.brandVoice) : null;

      // Prepare data with only Updateable fields and non-empty values
      const data = {
        Id: parseInt(id)
      };

      // Only include fields that have actual values (not empty strings or undefined)
      if (clientData.companyName !== undefined && clientData.companyName.trim() !== "") {
        data.Name = clientData.companyName.trim();
        data.company_name_c = clientData.companyName.trim();
      }
      if (clientData.clientInformation !== undefined && clientData.clientInformation.trim() !== "") {
        data.client_information_c = clientData.clientInformation.trim();
      }
      if (clientData.websiteUrl !== undefined && clientData.websiteUrl.trim() !== "") {
        data.website_url_c = clientData.websiteUrl.trim();
      }
      
      // Handle brand voice fields - only include non-empty values
if (brandVoice) {
        if (brandVoice.profileSummary && brandVoice.profileSummary.trim() !== "") {
          data.brand_voice_profile_summary_c = brandVoice.profileSummary.trim();
        }
        if (brandVoice.keywords && brandVoice.keywords.trim() !== "") {
          data.brand_voice_keywords_c = brandVoice.keywords.trim();
        }
        if (brandVoice.hashtags && brandVoice.hashtags.trim() !== "") {
          data.brand_voice_hashtags_c = brandVoice.hashtags.trim();
        }
        if (brandVoice.voice && brandVoice.voice.trim() !== "") {
          data.brand_voice_voice_c = brandVoice.voice.trim();
        }
        if (brandVoice.targetAudience && brandVoice.targetAudience.trim() !== "") {
          data.brand_voice_target_audience_c = brandVoice.targetAudience.trim();
        }
        if (brandVoice.description && brandVoice.description.trim() !== "") {
          data.description_c = brandVoice.description.trim();
        }
        if (brandVoice.benefits && brandVoice.benefits.trim() !== "") {
          data.benefits_c = brandVoice.benefits.trim();
        }
        const serializedAudiences = serializeAudiences(brandVoice.audiences);
        if (serializedAudiences) {
          data.audiences_c = serializedAudiences;
        }
        if (brandVoice.campaignObjective && brandVoice.campaignObjective.trim() !== "") {
          data.campaign_objective_c = brandVoice.campaignObjective.trim();
        }
        if (brandVoice.offerCategory && brandVoice.offerCategory.trim() !== "") {
          data.offer_category_c = brandVoice.offerCategory.trim();
        }
        if (brandVoice.brandToneVoice && brandVoice.brandToneVoice.trim() !== "") {
          data.brand_tone_voice_c = brandVoice.brandToneVoice.trim();
        }
        if (brandVoice.topCustomerDesires && brandVoice.topCustomerDesires.trim() !== "") {
          data.top_customer_desires_c = brandVoice.topCustomerDesires.trim();
        }
        if (brandVoice.primaryCustomerProblems && brandVoice.primaryCustomerProblems.trim() !== "") {
          data.primary_customer_problems_c = brandVoice.primaryCustomerProblems.trim();
        }
        if (brandVoice.proofAssets && brandVoice.proofAssets.trim() !== "") {
          data.proof_assets_c = brandVoice.proofAssets.trim();
        }
        
        // Store extended brand voice properties
        const extendedBrandVoice = serializeExtendedBrandVoice(brandVoice);
        if (extendedBrandVoice) {
          data.hidden_link_c = extendedBrandVoice;
        }
      }

      // Validate that we have updateable fields beyond just the ID
      const updateableFieldsCount = Object.keys(data).filter(key => key !== 'Id').length;
      if (updateableFieldsCount === 0) {
        console.error('Failed to update client: No updateable fields provided', { data, originalId: id });
        throw new Error("No updateable fields provided for update");
      }

      const response = await apperClient.updateRecord(this.tableName, {
        records: [data]
      });

      if (!response.success) {
        console.error("Failed to update client:", response);
        throw new Error(response.message || "Failed to update client");
      }

      if (response.results) {
        const successful = response.results.filter(r => r.success);
        const failed = response.results.filter(r => !r.success);
        
        if (failed.length > 0) {
          console.error(`Failed to update ${failed.length} clients:`, failed);
          failed.forEach(record => {
            if (record.errors) {
              record.errors.forEach(error => console.error(`${error.fieldLabel}: ${error}`));
            }
          });
        }
        
        if (successful.length > 0) {
          const updated = successful[0].data;
          return {
            Id: updated.Id,
            companyName: updated.company_name_c || updated.Name || "",
            clientInformation: updated.client_information_c || "",
            websiteUrl: updated.website_url_c || "",
brandVoice: normalizeBrandVoice({
              profileSummary: updated.brand_voice_profile_summary_c || "",
              keywords: updated.brand_voice_keywords_c || "",
              hashtags: updated.brand_voice_hashtags_c || "",
              voice: updated.brand_voice_voice_c || "",
              targetAudience: updated.brand_voice_target_audience_c || "",
              description: updated.description_c || "",
              benefits: updated.benefits_c || "",
              audiences: parseAudiences(updated.audiences_c),
              campaignObjective: updated.campaign_objective_c || "",
              offerCategory: updated.offer_category_c || "",
              brandToneVoice: updated.brand_tone_voice_c || "",
              topCustomerDesires: updated.top_customer_desires_c || "",
              primaryCustomerProblems: updated.primary_customer_problems_c || "",
              proofAssets: updated.proof_assets_c || "",
              ...parseExtendedBrandVoice(updated.hidden_link_c || "")
            }),
          websiteUrl: updated.website_url_c || "",
            updatedAt: updated.ModifiedOn || new Date().toISOString()
          };
        }
      }

      throw new Error("No successful records updated");
    } catch (error) {
      console.error("Error updating client:", error);
      throw error;
    }
  }

  async delete(id) {
    try {
      const apperClient = getApperClient();
      if (!apperClient) {
        throw new Error("ApperClient not available");
      }

      const response = await apperClient.deleteRecord(this.tableName, {
        RecordIds: [parseInt(id)]
      });

      if (!response.success) {
        console.error("Failed to delete client:", response);
        throw new Error(response.message || "Failed to delete client");
      }

      if (response.results) {
        const failed = response.results.filter(r => !r.success);
        
        if (failed.length > 0) {
          console.error(`Failed to delete ${failed.length} clients:`, failed);
          failed.forEach(record => {
            if (record.message) console.error(record.message);
          });
        }
        
        return response.results.filter(r => r.success).length > 0;
      }

      return true;
    } catch (error) {
      console.error("Error deleting client:", error);
      throw error;
    }
  }
}

export const clientService = new ClientService();